import { combineReducers } from 'redux';
import {
  CASE_GET_BY_CASE_ID_SUCCESS,
  FB_CASE_USER_GET_NEW_PARTICIPANT_SUCCESS,
  FB_CALENDAR_EVENT_GET_SUCCESS,
  MESSAGING_GROUP_CHAT_ADD_SUCCESS,
  FB_MESSAGING_GROUP_CHAT_GET_SUCCESS,
  MESSAGING_TOPIC_ADD_SUCCESS,
  FB_MESSAGING_TOPIC_GET_SUCCESS,
  MESSAGING_GROUP_CHAT_PRE_SIGN_URL_FOR_DOWNLOAD_SUCCESS,
  NOTIFICATIONS_GET_ALL_SUCCESS,
  NOTIFICATIONS_MARK_AS_READ_SUCCESS,
  NOTIFICATIONS_BADGE_NUMBER_CHANGED,
  FB_PROFILE_FOLLOWER_UPDATED_SUCCESS,
  FB_CALENDAR_EVENT_REMOVE_SUCCESS,
  AUTH_USER_SUCCESS,
  FB_PROFILE_OWN_USER_UPDATED_SUCCESS,
  CLEAR_ALL_STATE,
  UNAUTH_USER,
  FB_CASE_LEAVE,
  FB_CASE_GET_HEADER_INFO_SUCCESS,
  FB_REMOVE_PARTICIPANT,
  COMPONENT_GET_FILE_SERVER_SUCCESS,
  FB_EMPLOYER_GET_SUCCESS,
  COMPONENT_REMOVE_FILE_ENTRY_LOCALLY,
  CASE_GET_BY_USER_SUCCESS,
  REHYDRATE,
  EMPLOYER_UPDATE_INVITE_REQUEST_STATUS_SUCCESS,
  FB_REMOVE_GROUP_MESSAGE,
  FETCH_OUTBOUND_REFERRALS_SUCCESS,
  FETCH_INBOUND_REFERRALS_SUCCESS,
  FB_REMOVE_FOLLOWER,
  CALENDAR_FETCH_SUCCESS,
  CASE_SEARCH_SUCCESS,
} from '../actions/actionTypes';
import { timeZonesList } from './timeZonesList';
import { vaccinesByRegion, allVaccines } from './vaccines';
import supportedLocales from './locales';
import { fileComponentKey as cKey } from '../helpers/reducers';
import { findCaseIdForEventId } from '../helpers/entities_calendar';

import employerAdminReducer from './employerAdmin';
import patientsReducer from './entities_patients';

import { removeByKeyHelper, removeByListOfAttributeIdsHelper } from './helpers';
import { CAREGIVER, PATIENT, FAMILY, FRIEND } from '../constants/participant_roles';
import RANKS from './ranks';
import Utils from '../utils';

/* ----------------------------------------------- */
/* ----------------- CASES ----------------------- */
/* ----------------------------------------------- */
const CASES_INITIAL_STATE = {};

function merge(obj1, obj2) {
  return {
    ...obj1,
    ...obj2,
  };
}

function casesReducer(state = CASES_INITIAL_STATE, action) {
  switch (action.type) {
    case CASE_SEARCH_SUCCESS:
      return {
        ...action.payload?.entities?.cases,
        ...state,
      };
    case FB_CASE_GET_HEADER_INFO_SUCCESS:
    case CASE_GET_BY_CASE_ID_SUCCESS:
      let currentCaseState = state[action.payload.caseId] || {};
      return {
        ...state,
        [action.payload.caseId]: {
          caseId: action.payload.caseId,
          employerId: action.payload.employerId,
          description: action.payload.description,
          name: action.payload.name,
          patientName: action.payload.patientName,
          patientId: action.payload.patientId,
          participants: action.payload.participants || currentCaseState.participants,
          updated: action.payload.updated,
          ailment: action.payload.ailment,
          timeZone: action.payload.timeZone,
          clinical_location_id: action.payload.clinical_location_id,
          start_date: action.payload.start_date,
          group: action.payload.group,
        },
      };
    case FB_CASE_USER_GET_NEW_PARTICIPANT_SUCCESS:
      let caseId = action.payload.caseId;
      let userId = action.payload.userId;
      let participantInfo = {
        userId: userId,
        roleId: action.payload.roleId,
        updated: action.payload.updatedParticipant,
        isMedicalTeam: action.payload.isMedicalTeam,
      };
      delete action.payload.caseId;
      return {
        ...state,
        [caseId]: {
          ...state[caseId],
          participants: {
            ...state[caseId].participants,
            [action.payload.userId]: participantInfo,
          },
        },
      };
    case FB_CASE_LEAVE:
      return removeByKeyHelper(state, action.payload.toString());
    case FB_REMOVE_PARTICIPANT:
      return {
        ...state,
        [action.payload.caseId]: {
          ...state[action.payload.caseId],
          participants: removeByKeyHelper(
            state[action.payload.caseId].participants,
            action.payload.userId
          ),
        },
      };
    default:
      if (action.payload && action.payload.entities && action.payload.entities.cases) {
        return merge(state, action.payload.entities.cases);
      }
      return state;
  }

  return state;
}

/* -------------------------------------------------- */
/* ----------------- CALENDAR ----------------------- */
/* -------------------------------------------------- */
const CALENDAR_INITIAL_STATE = {};

function _sortEventsByCaseId(events) {
  const eventsByCase = {};
  Object.keys(events).forEach((eventId) => {
    const eventObj = events[eventId];
    const caseId = eventObj.caseId;
    if (!eventsByCase[caseId]) eventsByCase[caseId] = {};
    eventsByCase[caseId][eventId] = eventObj;
  });
  return eventsByCase;
}

function calendarReducer(state = CALENDAR_INITIAL_STATE, action) {
  switch (action.type) {
    //case CALENDAR_EVENT_ADD_SUCCESS:
    // case CALENDAR_EVENT_EDIT_SUCCESS:
    // We remove this because if we edit a recurring event the eventId returned by the server is the new event
    // and we don't want to update that id with the wrong payload.
    case FB_CALENDAR_EVENT_GET_SUCCESS:
      return {
        ...state,
        [action.payload.caseId]: {
          ...state[action.payload.caseId],
          [action.payload.eventId]: action.payload,
        },
      };
    case FB_CALENDAR_EVENT_REMOVE_SUCCESS:
      //if eventId is not a string change convert it to string. Otherwise we won't find it in the dictionary
      const eventId = action.payload.toString();
      const caseId = findCaseIdForEventId(state, eventId);

      // check to make sure the event/case actually existsing in the calendar
      // redux before attempting to remove it
      if (!caseId || !state[caseId]) return state;

      return {
        ...state,
        [caseId]: removeByKeyHelper(state[caseId], eventId),
      };
    case CASE_GET_BY_CASE_ID_SUCCESS:
      return {
        ...state,
        [action.payload.caseId]: {
          ...state[action.payload.caseId],
          ...action.payload.calendar,
        },
      };
    case FB_CASE_LEAVE:
      return removeByKeyHelper(state, action.payload.toString());
    case REHYDRATE:
      // This is when we rehydrate from the state persisted in the local DB
      if (action.payload && action.payload.entities && action.payload.entities.calendar) {
        return merge(state, action.payload.entities.calendar);
      }
      return state;
    case CALENDAR_FETCH_SUCCESS:
    case CASE_GET_BY_USER_SUCCESS:
      // this is when we get all of the data back from the server on login
      if (action.payload.entities && action.payload.entities.calendar) {
        const calendar = _sortEventsByCaseId(action.payload.entities.calendar);
        return merge(state, calendar);
      }
      return state;
    default:
      return state;
  }

  return state;
}

/* -------------------------------------------------- */
/* ----------------- METADATA (types) ----------------------- */
/* -------------------------------------------------- */
const METADATA_INITIAL_STATE = {
  caseRoles: [
    { id: CAREGIVER, name: 'Caregiver' },
    { id: PATIENT, name: 'Patient' },
    { id: FAMILY, name: 'Family' },
    { id: FRIEND, name: 'Friend' },
  ],
  timeZones: timeZonesList(),
  locales: { ...supportedLocales },
  vaccinesByRegion,
  allVaccines,
  ranks: RANKS,
  vaRegions: [
    { id: 1, name: 'Region 1' },
    { id: 2, name: 'Region 2' },
    { id: 3, name: 'Region 3' },
    { id: 4, name: 'Region 4' },
    { id: 5, name: 'Region 5' },
    { id: 6, name: 'Region 6' },
  ],
};

function metaDataReducer(state = METADATA_INITIAL_STATE, action) {
  switch (action.type) {
    default:
      if (action.payload && action.payload.entities && action.payload.entities.metaData) {
        return merge(state, action.payload.entities.metaData);
      }
      return state;
  }
}

const TOPICS_INITIAL_STATE = {};

function topicsReducer(state = TOPICS_INITIAL_STATE, action) {
  switch (action.type) {
    case MESSAGING_GROUP_CHAT_ADD_SUCCESS:
    case FB_MESSAGING_GROUP_CHAT_GET_SUCCESS:
      if (!state[action.payload.topicId]) {
        return state;
      }

      const newMessage = { [action.payload.messageId]: action.payload };
      const newMessages = merge(state[action.payload.topicId].messages, newMessage);
      return {
        ...state,
        [action.payload.topicId]: {
          ...state[action.payload.topicId],
          messages: newMessages,
        },
      };
    case FB_REMOVE_GROUP_MESSAGE:
      const { topicId, messageId } = action.payload;
      return {
        ...state,
        [topicId]: {
          ...state[topicId],
          messages: removeByKeyHelper(state[topicId].messages, messageId),
        },
      };
    case MESSAGING_GROUP_CHAT_PRE_SIGN_URL_FOR_DOWNLOAD_SUCCESS:
      const updatedMessage = {
        [action.payload.messageId]: {
          ...action.payload,
          fileUrl: action.payload.url,
        },
      };
      const updatedMessages = merge(state[action.payload.topicId].messages, updatedMessage);
      return {
        ...state,
        [action.payload.topicId]: {
          ...state[action.payload.topicId],
          messages: updatedMessages,
        },
      };
    case MESSAGING_TOPIC_ADD_SUCCESS:
    case FB_MESSAGING_TOPIC_GET_SUCCESS:
      return {
        ...state,
        [action.payload.topicId]: action.payload,
      };
    case FB_CASE_LEAVE:
      return removeByListOfAttributeIdsHelper(state, action.payload, 'caseId');
    // FullSync
    default:
      if (action.payload && action.payload.entities && action.payload.entities.topics) {
        return merge(state, action.payload.entities.topics);
      }
      return state;
  }

  return state;
}

const FOLLOWERS_INITIAL_STATE = {};

function followersReducer(state = FOLLOWERS_INITIAL_STATE, action) {
  switch (action.type) {
    case FB_REMOVE_FOLLOWER:
      const newState = { ...state };
      delete newState[action.payload.userId];
      return newState;
    case FB_PROFILE_FOLLOWER_UPDATED_SUCCESS:
    case FB_PROFILE_OWN_USER_UPDATED_SUCCESS:
      return {
        ...state,
        [action.payload.userId]: {
          ...state[action.payload.userId],
          fullName: action.payload.fullName,
          friendlyName: Utils.friendlyName(action.payload.fullName),
          avatarUrl: action.payload.avatarUrl,
          updated: action.payload.updated || action.payload.updatedUser,
          userId: action.payload.userId,
          employers: action.payload.employers,
          phone: action.payload.phone,
        },
      };
    case CASE_GET_BY_CASE_ID_SUCCESS:
      return {
        ...state,
        ...action.payload.followers,
      };
    // FullSync
    default:
      if (action.payload && action.payload.entities && action.payload.entities.followers) {
        return merge(state, action.payload.entities.followers);
      }
      return state;
  }

  return state;
}

const NOTIFICATIONS_INITIAL_STATE = { notifications: {}, badge: 0 };

function notificationsReducer(state = NOTIFICATIONS_INITIAL_STATE, action) {
  switch (action.type) {
    case NOTIFICATIONS_GET_ALL_SUCCESS:
      return {
        ...state,
        notifications: action.payload,
        badge: Object.keys(action.payload || {}).length,
      };
    case NOTIFICATIONS_MARK_AS_READ_SUCCESS:
      //Since we only show unread notifications we want to remove the notification
      const newNotifications = removeByKeyHelper(
        state.notifications,
        action.payload.notificationId
      );
      return { notifications: newNotifications, badge: state.badge };
    case NOTIFICATIONS_BADGE_NUMBER_CHANGED:
      return merge(state, {
        notifications: state.notifications,
        badge: action.payload,
      });
    // FullSync
    default:
      if (action.payload && action.payload.entities && action.payload.entities.notifications) {
        return merge(state, action.payload.entities.notifications);
      }
      return state;
  }

  return state;
}

const USER_PROFILE_INITIAL_STATE = null;
function userProfileReducer(state = USER_PROFILE_INITIAL_STATE, action) {
  switch (action.type) {
    case AUTH_USER_SUCCESS:
    case FB_PROFILE_OWN_USER_UPDATED_SUCCESS:
      return {
        ...state,
        userId: action.payload.userId,
        email: action.payload.email,
        fullName: action.payload.fullName,
        avatarUrl: action.payload.avatarUrl,
        friendlyName: Utils.friendlyName(action.payload.fullName),
        phone: action.payload.phone,
        opOutTracking: action.payload.opOutTracking,
        userPermissions: action.payload.userPermissions,
        updated: action.payload.updated,
        locale: action.payload.locale,
      };
    default:
      if (action.payload && action.payload.entities && action.payload.entities.userProfile) {
        return merge(state, action.payload.entities.userProfile);
      }
      return state;
  }
  return state;
}

const EMPLOYERS_INITIAL_STATE = {};
function employersReducer(state = EMPLOYERS_INITIAL_STATE, action) {
  let employerId;
  switch (action.type) {
    case AUTH_USER_SUCCESS:
    // FB updates are sent to the user profile when user-employer related data is updated
    // such as the users permissions, or superadmin status
    case FB_PROFILE_OWN_USER_UPDATED_SUCCESS:
      return Object.keys(action.payload.employers || {}).reduce(
        (result, employerId) => {
          result[employerId] = {
            ...result[employerId],
            ...action.payload.employers[employerId],
          };
          return result;
        },
        { ...state }
      );
    // case FB_EMPLOYER_LEAVE:
    //   return removeByKeyHelper(state, action.payload.toString());
    case FB_EMPLOYER_GET_SUCCESS:
      employerId = action.payload.employerId;
      if (!employerId) return state;

      // just overwrite the fields that are returned with the employerInfo, but dont replace the
      // entire entity because the data such as permissions and superadmin status are not included
      // in the employer info with this action (only in the user profile related actions)
      return {
        ...state,
        [employerId]: {
          ...state[employerId],
          ...action.payload,
        },
      };

    case EMPLOYER_UPDATE_INVITE_REQUEST_STATUS_SUCCESS:
      employerId = action.payload.employerId;
      const requestId = action.payload.requestId;
      let status = action.payload.status;

      if (!employerId || !requestId || status === null) return state;

      if (state[employerId]?.inviteRequests?.length == null) return state;

      const irs = state[employerId]?.inviteRequests?.map((ir) => {
        if (ir.request_id === requestId) {
          return {
            ...ir,
            status,
          };
        }
        return ir;
      });

      if (!irs) return state;

      return {
        ...state,
        [employerId]: {
          ...state[employerId],
          inviteRequests: irs,
        },
      };

    default:
      // if (action.payload && action.payload.entities && action.payload.entities.employers) {
      //   return merge(state, action.payload.entities.employers);
      // }
      return state;
  }
}

const APP_SETTINGS_INITIAL_STATE = {
  allowAccessToNativeCalendar: null,
  timezone: 'America/Los_Angeles',
};

function appSettingsReducer(state = APP_SETTINGS_INITIAL_STATE, action) {
  switch (action.type) {
    case AUTH_USER_SUCCESS:
    case FB_PROFILE_OWN_USER_UPDATED_SUCCESS:
      return {
        ...state,
        allowAccessToNativeCalendar: action.payload.syncNativeCal,
        timezone: action.payload.timezone || 'America/Los_Angeles',
        updated: action.payload.updated,
      };
    default:
      if (action.payload && action.payload.entities && action.payload.entities.appSettings) {
        return merge(state, action.payload.entities.appSettings);
      }
      return state;
  }
  return state;
}

const FILES_INITIAL_STATE = {};

function filesReducer(state = FILES_INITIAL_STATE, action) {
  switch (action.type) {
    //case COMPONENT_ADD_FILE_SERVER_SUCCESS:
    case COMPONENT_GET_FILE_SERVER_SUCCESS:
      return {
        ...state,
        [cKey(action.payload)]: action.payload,
      };
    case COMPONENT_REMOVE_FILE_ENTRY_LOCALLY:
      return removeByKeyHelper(state, cKey(action.payload));
    // FullSync
    default:
      if (action.payload && action.payload.entities && action.payload.entities.files) {
        return merge(state, action.payload.entities.files);
      }
      return state;
  }
}

const CONSULTS_INITIAL_STATE = {};
function consultsReducer(state = CONSULTS_INITIAL_STATE, action) {
  switch (action.type) {
    // case CONSULT_REFERRAL_SEND_SUCCESS:
    //   console.info('>>>> CONSULT_REFERRAL_SEND_SUCCESS', action.payload)
    //   return {
    //     ...state,
    //     outbound: {
    //       ...state.outbound,
    //       ...(action.payload || {})
    //     }
    //   }
    case FETCH_OUTBOUND_REFERRALS_SUCCESS:
      return {
        ...state,
        outbound: {
          ...action.payload?.entities?.consults?.outbound,
        },
      };
    case FETCH_INBOUND_REFERRALS_SUCCESS:
      return {
        ...state,
        inbound: {
          ...action.payload?.entities?.consults?.inbound,
        },
      };
    default:
      return state;
  }
}

const subEntitiesReducer = combineReducers({
  cases: casesReducer,
  calendar: calendarReducer,
  metaData: metaDataReducer,
  topics: topicsReducer,
  followers: followersReducer,
  patients: patientsReducer,
  notifications: notificationsReducer,
  userProfile: userProfileReducer,
  appSettings: appSettingsReducer,
  files: filesReducer,
  employerAdmin: employerAdminReducer,
  employers: employersReducer,
  consults: consultsReducer,
});

const entitiesReducer = (state, action) => {
  if (action.type === CLEAR_ALL_STATE || action.type === UNAUTH_USER) {
    // Clear state on logout if there is a new app version
    state = undefined;
  }

  return subEntitiesReducer(state, action);
};

export default entitiesReducer;
